import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TestInviteFunction = () => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [testMode, setTestMode] = useState<"get" | "post">("get");
  const [inviteType, setInviteType] = useState<"parent" | "staff">("parent");
  const [timeout, setTimeout] = useState(15000);
  const [useCredentials, setUseCredentials] = useState(false);
  const [additionalHeaders, setAdditionalHeaders] = useState<{[key: string]: string}>({});
  const [password, setPassword] = useState("");

  // Supabase anon key constant - required for public API access
  const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im11b2loemZvbWZlcmNzdWp5eXRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM4Nzc5ODYsImV4cCI6MjA1OTQ1Mzk4Nn0.yOlwlmjSRiqWhjEPWgCg_jvqLEgvfzQacDhezJlQZ98";
  const SUPABASE_URL = "https://muoihzfomfercsujyyta.supabase.co";

  // Add a log function that stores logs for debugging
  const addLog = (message: string) => {
    console.log(message);
    setLogs(prev => [...prev, `${new Date().toISOString()} - ${message}`]);
  };

  const clearLogs = () => {
    setLogs([]);
    setResult(null);
  };

  const testFunction = async () => {
    if (!token) {
      addLog("No token provided");
      return;
    }

    setLoading(true);
    clearLogs();
    addLog(`Testing ${inviteType}-invite function with token: ${token}`);
    addLog(`Mode: ${testMode.toUpperCase()}`);
    addLog(`Timeout: ${timeout}ms`);
    addLog(`Use credentials: ${useCredentials}`);

    try {
      // Create an AbortController for timeout
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort();
        addLog("Request timed out after specified timeout");
      }, timeout);

      // Build the request URL based on invite type
      const functionName = inviteType === 'staff' ? 'validate-staff-invite' : 'validate-invite';
      let requestUrl = `${SUPABASE_URL}/functions/v1/${functionName}`;
      if (testMode === "get") {
        requestUrl += `?token=${encodeURIComponent(token)}`;
      }
      addLog(`Making request to: ${requestUrl}`);
      
      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Origin': window.location.origin,
        ...additionalHeaders
      };

      addLog(`Request headers: ${JSON.stringify(headers)}`);
      
      // Prepare fetch options
      const fetchOptions: RequestInit = {
        method: testMode === "get" ? 'GET' : 'POST',
        headers,
        signal: controller.signal,
        credentials: useCredentials ? 'include' : 'same-origin'
      };

      // Add body for POST requests
      if (testMode === "post") {
        fetchOptions.body = JSON.stringify({ 
          token, 
          password: password || "TestPassword123!" 
        });
        addLog(`Request body: ${fetchOptions.body}`);
      }
      
      // Track timing
      const startTime = performance.now();
      
      // NEVER log request body for security-sensitive operations
      const logUrl = requestUrl.includes('?') ? requestUrl.substring(0, requestUrl.indexOf('?')) + '?[params-redacted]' : requestUrl;
      console.log('[DEBUG] About to execute fetch:', logUrl, {
        ...fetchOptions,
        body: fetchOptions.body ? '[Request body redacted for security]' : undefined
      });
      
      // Make the fetch request with timeout
      const response = await fetch(requestUrl, fetchOptions);
      
      // Clear timeout since request completed
      window.clearTimeout(timeoutId);
      
      const endTime = performance.now();
      addLog(`Response received in ${Math.round(endTime - startTime)}ms: status=${response.status}`);

      // Get response headers
      const headers_response: Record<string, string> = {};
      response.headers.forEach((value, name) => {
        headers_response[name] = value;
        addLog(`Response header: ${name}: ${value}`);
      });
      
      // Get response body
      const responseText = await response.text();
      addLog(`Response body received (${responseText.length} chars)`);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
        addLog("Successfully parsed response as JSON");
      } catch (error) {
        addLog("Failed to parse response as JSON");
        responseData = { raw: responseText };
      }
      
      setResult({
        success: response.ok,
        status: response.status,
        headers: headers_response,
        data: responseData,
        timing: {
          start: startTime,
          end: endTime,
          duration: Math.round(endTime - startTime)
        }
      });
      
    } catch (error: any) {
      // Check if this was an abort error (timeout)
      if (error.name === 'AbortError') {
        addLog("Request was aborted due to timeout");
        setResult({
          success: false,
          error: `Request timed out after ${timeout}ms`
        });
      } else {
        addLog(`Error: ${error.message}`);
        setResult({
          success: false,
          error: error.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-10">
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Test Invitation Functions</CardTitle>
          <CardDescription>
            Use this tool to test both parent and staff invitation Edge Functions directly
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Test</TabsTrigger>
              <TabsTrigger value="advanced">Advanced Options</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h4 className="font-medium text-blue-900 mb-2">How to get a valid token:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><strong>Parent Invite:</strong> Create a parent invitation through the bizadmin dashboard, check the browser console for the generated link</li>
                  <li><strong>Staff Invite:</strong> Create a staff invitation through the bizadmin dashboard, check the browser console for the generated link</li>
                  <li>Extract the token parameter from the generated link (e.g., ?token=abc123...)</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="token">Invitation Token</Label>
                <Input
                  id="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter invitation token from console logs"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Label htmlFor="test-mode">Test Mode:</Label>
                <Select value={testMode} onValueChange={(value) => setTestMode(value as "get" | "post")}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="get">GET (Validate)</SelectItem>
                    <SelectItem value="post">POST (Set Password)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Label htmlFor="invite-type">Invite Type:</Label>
                <Select value={inviteType} onValueChange={(value) => setInviteType(value as "parent" | "staff")}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent Invite</SelectItem>
                    <SelectItem value="staff">Staff Invite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {testMode === "post" && (
                <div className="space-y-2">
                  <Label htmlFor="password">Test Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter test password"
                  />
                </div>
              )}
              
              <Button 
                onClick={testFunction} 
                disabled={loading || !token}
                className="w-full"
              >
                {loading ? "Testing..." : "Test Function"}
              </Button>
            </TabsContent>
            
            <TabsContent value="advanced" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="timeout">Request Timeout (ms)</Label>
                <Input
                  id="timeout"
                  type="number"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value) || 15000)}
                  min={1000}
                  max={60000}
                  step={1000}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="use-credentials"
                  checked={useCredentials}
                  onCheckedChange={setUseCredentials}
                />
                <Label htmlFor="use-credentials">Include Credentials</Label>
              </div>
              
              <div className="space-y-2">
                <Label>Additional Headers</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Header Name"
                    onChange={(e) => {
                      const headerName = e.target.value;
                      const headerInput = document.getElementById('header-value') as HTMLInputElement;
                      const headerValue = headerInput?.value || '';
                      
                      if (headerName && headerValue) {
                        setAdditionalHeaders(prev => ({
                          ...prev,
                          [headerName]: headerValue
                        }));
                      }
                    }}
                  />
                  <Input
                    id="header-value"
                    placeholder="Header Value"
                    onChange={(e) => {
                      const headerValue = e.target.value;
                      const headerInput = document.getElementById('header-name') as HTMLInputElement;
                      const headerName = headerInput?.value || '';
                      
                      if (headerName && headerValue) {
                        setAdditionalHeaders(prev => ({
                          ...prev,
                          [headerName]: headerValue
                        }));
                      }
                    }}
                  />
                </div>
                
                {Object.keys(additionalHeaders).length > 0 && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium mb-1">Custom Headers:</p>
                    {Object.entries(additionalHeaders).map(([key, value]) => (
                      <div key={key} className="text-xs flex justify-between">
                        <span className="font-mono">{key}:</span>
                        <span className="font-mono text-gray-600">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button 
                onClick={testFunction} 
                disabled={loading || !token}
                className="w-full"
              >
                {loading ? "Testing..." : "Test Function with Advanced Options"}
              </Button>
            </TabsContent>
          </Tabs>
          
          {result && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Result</h3>
                <div className={`px-2 py-1 rounded text-xs ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {result.success ? 'Success' : 'Failed'}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-md overflow-auto max-h-60">
                <pre className="text-xs">{JSON.stringify(result, null, 2)}</pre>
              </div>
            </div>
          )}
          
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Logs</h3>
              <Button variant="outline" size="sm" onClick={clearLogs}>Clear</Button>
            </div>
            <div className="p-4 bg-gray-50 rounded-md overflow-auto max-h-60">
              {logs.map((log, i) => (
                <div key={i} className="text-xs font-mono">{log}</div>
              ))}
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between">
          <div className="text-sm text-gray-500">
            This tool is for debugging purposes only.
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href="https://supabase.com/dashboard/project/muoihzfomfercsujyyta/functions" target="_blank" rel="noopener noreferrer">
              View Edge Functions
            </a>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default TestInviteFunction; 